/**
 * Friend Leaderboard Component
 * VOY-133: Social competition against friends
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Share2, UserPlus } from 'lucide-react';

interface FriendEntry {
  rank: number;
  username: string;
  score: number;
  isYou?: boolean;
}

interface FriendLeaderboardProps {
  friends: FriendEntry[];
  onChallengeFriend: (username: string) => void;
  onShareScore: () => void;
  onInviteFriends: () => void;
}

export function FriendLeaderboard({ 
  friends, 
  onChallengeFriend, 
  onShareScore,
  onInviteFriends 
}: FriendLeaderboardProps) {
  return (
    <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border-white/30 dark:border-zinc-700/50 border-2 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#5B6FED] dark:text-blue-400" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Friend Leaderboard</h3>
          </div>
          <Button
            onClick={onInviteFriends}
            variant="outline"
            size="sm"
            className="rounded-full border-[#5B6FED] dark:border-blue-500 text-[#5B6FED] dark:text-blue-400 dark:hover:bg-zinc-700"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Invite
          </Button>
        </div>

        <p className="text-sm text-gray-600 dark:text-zinc-400 mb-4">
          See how you rank vs your crew
        </p>

        {/* Friend Rankings */}
        <div className="space-y-2 mb-4">
          {friends.map((friend) => (
            <div
              key={friend.username}
              className={`flex items-center justify-between p-3 rounded-xl ${
                friend.isYou 
                  ? 'bg-purple-50 dark:bg-zinc-700/50 border-2 border-[#5B6FED] dark:border-blue-500' 
                  : 'bg-gray-50 dark:bg-zinc-800/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`font-bold text-lg ${
                  friend.rank === 1 ? 'text-yellow-500' :
                  friend.rank === 2 ? 'text-gray-400 dark:text-zinc-400' :
                  friend.rank === 3 ? 'text-amber-700 dark:text-amber-500' :
                  'text-gray-400 dark:text-zinc-400'
                }`}>
                  {friend.rank === 1 ? '🥇' : 
                   friend.rank === 2 ? '🥈' : 
                   friend.rank === 3 ? '🥉' : 
                   `${friend.rank}.`}
                </span>
                <div>
                  <p className={`font-medium ${friend.isYou ? 'text-[#5B6FED] dark:text-blue-400 font-bold' : 'dark:text-white'}`}>
                    {friend.username}
                    {friend.isYou && <span className="ml-2 text-xs">YOU</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <p className="font-bold text-lg dark:text-white">{friend.score.toLocaleString()}</p>
                {!friend.isYou && (
                  <Button
                    onClick={() => onChallengeFriend(friend.username)}
                    size="sm"
                    variant="outline"
                    className="rounded-full text-xs"
                  >
                    Challenge
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Motivation Message */}
        {friends.length > 0 && friends[0] && !friends[0].isYou && (
          <div className="bg-blue-50 rounded-lg p-3 mb-3">
            <p className="text-sm text-blue-900">
              💬 <strong>Beat {friends[0].username}</strong> for a free booster!
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={onShareScore}
            variant="outline"
            className="flex-1 rounded-full border-[#5B6FED] text-[#5B6FED]"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Score
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

